'use client';

import {
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Portal,
  Text,
  VisuallyHidden,
  VStack,
} from '@chakra-ui/react';
import { CalendarClock, Check, Paperclip, Plus, Sparkles, X } from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useAuth } from '@/app/auth-provider';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { Menu, Popover } from '@chakra-ui/react';
import { Tooltip } from '@/components/ui/tooltip';
import type {
  AgentSkillFeatureControls,
  AgentSkillScope,
  AgentSkillUsage,
  EffectiveAgentSkill,
} from '@/lib/agent-skills/types';

export type SkillPickerItem = {
  skillId: string;
  versionId: string;
  version: number;
  scope: AgentSkillScope;
  handle: string;
  qualifiedHandle: string;
  displayName: string;
  description: string;
  status: 'draft' | 'published' | 'archived';
  allowImplicitInvocation: boolean;
  pinned: boolean;
  isAmbiguous: boolean;
};

const DEFAULT_CONTROLS: AgentSkillFeatureControls = {
  managementEnabled: true,
  explicitInvocationEnabled: true,
  implicitInvocationEnabled: true,
  tenantSkillsEnabled: true,
};

type EffectiveSkillsState = {
  skills: SkillPickerItem[];
  controls: AgentSkillFeatureControls;
};

const effectiveSkillsCache = new Map<string, EffectiveSkillsState>();
const effectiveSkillsRequests = new Map<string, Promise<EffectiveSkillsState>>();

function normalizeEffectiveSkill(value: unknown): SkillPickerItem | null {
  if (!value || typeof value !== 'object') return null;
  const entry = value as Partial<EffectiveAgentSkill> & Record<string, unknown>;
  if (
    !entry.skill ||
    typeof entry.skill !== 'object' ||
    !entry.version ||
    typeof entry.version !== 'object'
  ) {
    return null;
  }
  const skill = entry.skill as EffectiveAgentSkill['skill'];
  const version = entry.version as EffectiveAgentSkill['version'];
  if (!skill.id || !version.id) return null;
  const qualifiedHandle =
    typeof entry.qualifiedHandle === 'string'
      ? entry.qualifiedHandle
      : `@${skill.scopeType}:${skill.slug}`;
  const handle = typeof entry.handle === 'string' ? entry.handle : `@${skill.slug}`;
  return {
    skillId: skill.id,
    versionId: version.id,
    version: version.version,
    scope: skill.scopeType,
    handle,
    qualifiedHandle,
    displayName: skill.displayName,
    description: skill.description,
    status: skill.status,
    allowImplicitInvocation:
      typeof entry.allowImplicitInvocation === 'boolean'
        ? entry.allowImplicitInvocation
        : skill.allowImplicitInvocation,
    pinned: Boolean(entry.isPinned),
    isAmbiguous: Boolean(entry.isAmbiguous),
  };
}

function normalizeEffectiveSkillsPayload(payload: unknown): EffectiveSkillsState {
  if (!payload || typeof payload !== 'object') {
    return { skills: [], controls: DEFAULT_CONTROLS };
  }
  const record = payload as Record<string, unknown>;
  const source = Array.isArray(record.skills)
    ? record.skills
    : Array.isArray(record.effectiveSkills)
      ? record.effectiveSkills
      : [];
  const normalized = source
    .map(normalizeEffectiveSkill)
    .filter((item): item is SkillPickerItem => Boolean(item));
  return {
    skills: normalized,
    controls:
      record.controls && typeof record.controls === 'object'
        ? { ...DEFAULT_CONTROLS, ...(record.controls as Partial<AgentSkillFeatureControls>) }
        : DEFAULT_CONTROLS,
  };
}

async function loadEffectiveSkills(
  scopeId: string,
  userKey: string,
  getToken: () => Promise<string>,
  scopeType: 'project' | 'workspace' = 'project',
): Promise<EffectiveSkillsState> {
  const cacheKey = `${userKey}:${scopeType}:${scopeId}`;
  const cached = effectiveSkillsCache.get(cacheKey);
  if (cached) return cached;
  const pending = effectiveSkillsRequests.get(cacheKey);
  if (pending) return pending;

  const request = (async () => {
    const token = await getToken();
    const response = await fetch(
      `/api/${scopeType === 'workspace' ? 'workspaces' : 'projects'}/${encodeURIComponent(scopeId)}/skills?effective=true`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as {
        error?: string | { message?: string };
        message?: string;
      } | null;
      const message =
        typeof payload?.error === 'string'
          ? payload.error
          : payload?.error && typeof payload.error === 'object'
            ? payload.error.message
            : payload?.message;
      throw new Error(message || 'Skills are unavailable right now.');
    }
    const normalized = normalizeEffectiveSkillsPayload(await response.json());
    effectiveSkillsCache.set(cacheKey, normalized);
    return normalized;
  })().finally(() => effectiveSkillsRequests.delete(cacheKey));
  effectiveSkillsRequests.set(cacheKey, request);
  return request;
}

export function invalidateEffectiveSkillsCache(projectId?: string): void {
  if (!projectId) {
    effectiveSkillsCache.clear();
    return;
  }
  for (const key of effectiveSkillsCache.keys()) {
    if (key.endsWith(`:${projectId}`)) effectiveSkillsCache.delete(key);
  }
}

export function useEffectiveAgentSkills(projectId: string | null, workspaceId?: string | null): {
  skills: SkillPickerItem[];
  controls: AgentSkillFeatureControls;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const { user } = useAuth();
  const scopeId = workspaceId || projectId;
  const scopeType = workspaceId ? 'workspace' : 'project';
  const scopeKey = `${user?.uid || ''}:${scopeType}:${scopeId || ''}`;
  const [state, setState] = useState<EffectiveSkillsState & { scopeKey: string | null }>({
    skills: [],
    controls: DEFAULT_CONTROLS,
    scopeKey: null,
  });
  const [loading, setLoading] = useState(Boolean(scopeId));
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (!scopeId || !user) {
      setState({ skills: [], controls: DEFAULT_CONTROLS, scopeKey });
      setLoading(false);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void loadEffectiveSkills(scopeId, user.uid, () => user.getIdToken(), scopeType)
      .then(next => {
        if (!cancelled) setState({ ...next, scopeKey });
      })
      .catch(loadError => {
        if (cancelled) return;
        setState({ skills: [], controls: DEFAULT_CONTROLS, scopeKey });
        setError(
          loadError instanceof Error ? loadError.message : 'Skills are unavailable right now.'
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [scopeId, scopeType, scopeKey, revision, user]);

  const refresh = useCallback(() => {
    invalidateEffectiveSkillsCache(scopeId || undefined);
    setRevision(value => value + 1);
  }, [scopeId]);

  return {
    ...(state.scopeKey === scopeKey ? state : { skills: [], controls: DEFAULT_CONTROLS }),
    loading, error, refresh,
  };
}

export function getSkillTriggerQuery(value: string): string | null {
  const match = value.match(/(?:^|\s)@([a-zA-Z0-9:-]*)$/);
  return match ? match[1] : null;
}

export function removeSkillTrigger(value: string): string {
  return value.replace(/(?:^|\s)@[a-zA-Z0-9:-]*$/, '').replace(/\s+$/, '');
}

const SKILL_BADGE_STYLES = {
  neutral: {
    bg: 'status.neutral.bg',
    color: 'status.neutral.fg',
    borderColor: 'status.neutral.border',
  },
  info: {
    bg: 'status.info.bg',
    color: 'status.info.fg',
    borderColor: 'status.info.border',
  },
  system: {
    bg: 'accent.subtle',
    color: 'accent.fg',
    borderColor: 'accent.traceBorder',
  },
} as const;

function scopeBadgeStyle(scope: AgentSkillScope) {
  if (scope === 'project') return SKILL_BADGE_STYLES.info;
  if (scope === 'system') return SKILL_BADGE_STYLES.system;
  return SKILL_BADGE_STYLES.neutral;
}

export function SelectedSkillChips({
  skills,
  disabled = false,
  onRemove,
}: {
  skills: SkillPickerItem[];
  disabled?: boolean;
  onRemove: (skillId: string) => void;
}) {
  if (skills.length === 0) return null;
  return (
    <HStack gap="1.5" flexWrap="wrap" px="3" pt="3" aria-label="Selected Skills">
      {skills.map(skill => (
        <HStack
          key={`${skill.skillId}:${skill.versionId}`}
          gap="1"
          minH="28px"
          pl="2.5"
          pr="1"
          borderWidth="1px"
          borderColor="ui.border.subtle"
          borderRadius="glassPill"
          bg="ui.surface.input"
        >
          <Icon boxSize="3.5" color="accent.fg">
            <Sparkles />
          </Icon>
          <Text fontSize="xs" fontWeight="650" color="ui.text.primary" maxW="190px" truncate>
            {skill.handle}
          </Text>
          <Badge size="sm" borderWidth="1px" {...scopeBadgeStyle(skill.scope)}>
            v{skill.version}
          </Badge>
          <IconButton
            aria-label={`Remove ${skill.displayName}`}
            size="2xs"
            variant="ghost"
            borderRadius="full"
            disabled={disabled}
            onClick={() => onRemove(skill.skillId)}
          >
            <X size={12} />
          </IconButton>
        </HStack>
      ))}
    </HStack>
  );
}

function CompactSkillRowContent({
  skill,
  selected = false,
}: {
  skill: SkillPickerItem;
  selected?: boolean;
}) {
  return (
    <>
      <Icon boxSize="3.5" color={selected ? 'ui.text.tertiary' : 'accent.fg'} flexShrink={0}>
        <Sparkles />
      </Icon>
      <Text
        flex="1"
        minW={0}
        textAlign="start"
        fontSize="sm"
        fontWeight="550"
        letterSpacing="-0.01em"
        color={selected ? 'ui.text.tertiary' : 'ui.text.primary'}
        truncate
      >
        {skill.displayName}
      </Text>
      {selected ? (
        <HStack gap="1" color="ui.text.tertiary" flexShrink={0}>
          <Check size={12} strokeWidth={2} aria-hidden="true" />
          <Text fontSize="xs" fontWeight="600">
            Added
          </Text>
        </HStack>
      ) : null}
    </>
  );
}

function SkillDescriptionTooltip({
  skill,
  children,
}: {
  skill: SkillPickerItem;
  children: React.ReactElement;
}) {
  return (
    <Tooltip
      content={
        <VStack align="stretch" gap="1.5">
          <Text fontSize="xs" fontWeight="600" color="ui.text.primary">
            {skill.displayName}
          </Text>
          <Text fontSize="xs" lineHeight="1.55" color="ui.text.secondary" whiteSpace="normal">
            {skill.description.trim() || 'No description provided.'}
          </Text>
        </VStack>
      }
      openDelay={240}
      closeDelay={80}
      positioning={{
        placement: 'right-start',
        strategy: 'fixed',
        flip: true,
        slide: true,
        fitViewport: true,
        gutter: 8,
        overflowPadding: 12,
      }}
      contentProps={{
        layerStyle: 'surfacePopover',
        w: '280px',
        maxW: 'calc(100vw - 24px)',
        maxH: 'min(240px, calc(100vh - 24px))',
        overflowY: 'auto',
        p: '3',
        borderRadius: 'glassCard',
        borderWidth: '1px',
        borderColor: 'ui.border.default',
        boxShadow: 'var(--attune-shadow-floating, none)',
        zIndex: 'tooltip',
      }}
    >
      {children}
    </Tooltip>
  );
}

export function ComposerAddMenu({
  skills,
  selectedSkills,
  disabled = false,
  attachDisabled = false,
  selectionEnabled = true,
  onAttachFiles,
  onSelectSkill,
  onScheduleAgent,
}: {
  skills: SkillPickerItem[];
  selectedSkills: SkillPickerItem[];
  disabled?: boolean;
  attachDisabled?: boolean;
  selectionEnabled?: boolean;
  onAttachFiles: () => void;
  onScheduleAgent?: () => void;
  onSelectSkill: (skill: SkillPickerItem) => void;
}) {
  const selectedIds = useMemo(
    () => new Set(selectedSkills.map(skill => skill.skillId)),
    [selectedSkills]
  );
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  return (
    <Menu.Root
      positioning={{
        placement: 'top-start',
        strategy: 'fixed',
        flip: true,
        slide: true,
        sameWidth: false,
        fitViewport: true,
        gutter: 8,
        overflowPadding: 12,
        getAnchorRect: () => {
          const shell = triggerRef.current?.closest<HTMLElement>('[data-composer-shell]');
          return shell?.getBoundingClientRect() || null;
        },
      }}
    >
      <Menu.Trigger asChild>
        <IconButton
          ref={triggerRef}
          aria-label="Add files or Skills"
          size="sm"
          variant="ghost"
          borderRadius="glassPill"
          color="ui.text.tertiary"
          _hover={{ color: 'ui.text.primary', bg: 'ui.surface.input' }}
          disabled={disabled}
        >
          <Plus size={20} />
        </IconButton>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content
            layerStyle="surfacePopover"
            borderRadius="glassCard"
            w="320px"
            maxW="calc(100vw - 24px)"
            maxH="min(var(--available-height), 368px)"
            overflowY="auto"
            p="1"
            borderWidth="1px"
            borderColor="ui.border.subtle"
            boxShadow="var(--attune-shadow-floating, none)"
          >
            <Text
              px="2"
              pt="1"
              pb="1.5"
              fontSize="10px"
              lineHeight="1"
              fontWeight="700"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color="ui.text.tertiary"
            >
              Add
            </Text>
            <Menu.Item
              value="attach-files"
              onClick={onAttachFiles}
              disabled={attachDisabled}
              minH="36px"
              px="2"
              gap="2"
              borderRadius="glassInput"
              _highlighted={{ bg: 'ui.surface.selected' }}
            >
              <Icon boxSize="3.5" color="ui.text.secondary" flexShrink={0} aria-hidden="true">
                <Paperclip />
              </Icon>
              <Text fontSize="sm" fontWeight="550" letterSpacing="-0.01em" color="ui.text.primary">
                Files and images
              </Text>
            </Menu.Item>
            {onScheduleAgent ? (
              <Menu.Item
                value="schedule-agent"
                onSelect={onScheduleAgent}
                minH="36px"
                px="2"
                gap="2"
                borderRadius="glassInput"
                _highlighted={{ bg: 'ui.surface.selected' }}
              >
                <Icon boxSize="3.5" color="ui.text.secondary">
                  <CalendarClock />
                </Icon>
                <Text fontSize="sm" fontWeight="550">
                  Schedule an agent
                </Text>
              </Menu.Item>
            ) : null}
            <Box mt="1" pt="1" borderTopWidth="1px" borderColor="ui.border.subtle">
              <HStack px="2" pt="0.5" pb="1.5" justify="space-between">
                <Text
                  fontSize="10px"
                  lineHeight="1"
                  fontWeight="700"
                  letterSpacing="0.08em"
                  textTransform="uppercase"
                  color="ui.text.tertiary"
                >
                  Skills
                </Text>
                {skills.length > 0 ? (
                  <Text fontSize="10px" lineHeight="1" color="ui.text.tertiary">
                    {skills.length}
                  </Text>
                ) : null}
              </HStack>
              <Box maxH="260px" overflowY="auto" overscrollBehavior="contain">
                {skills.map(skill => {
                  const selected = selectedIds.has(skill.skillId);
                  return (
                    <SkillDescriptionTooltip
                      key={`${skill.skillId}:${skill.versionId}`}
                      skill={skill}
                    >
                      <Menu.Item
                        value={`skill-${skill.skillId}`}
                        disabled={selected || !selectionEnabled}
                        onClick={() => onSelectSkill(skill)}
                        minH="34px"
                        px="2"
                        gap="2"
                        borderRadius="glassInput"
                        opacity={selected ? 0.72 : 1}
                        _highlighted={{ bg: 'ui.surface.selected' }}
                      >
                        <CompactSkillRowContent skill={skill} selected={selected} />
                      </Menu.Item>
                    </SkillDescriptionTooltip>
                  );
                })}
                {skills.length === 0 ? (
                  <Text px="2" py="3" fontSize="xs" color="ui.text.secondary">
                    No Skills are available for this project.
                  </Text>
                ) : null}
              </Box>
            </Box>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

export function SkillsPicker({
  skills,
  selectedSkills,
  open,
  query,
  loading = false,
  error = null,
  onSelect,
  onClose,
  placement = 'top-start',
}: {
  skills: SkillPickerItem[];
  selectedSkills: SkillPickerItem[];
  open: boolean;
  query: string;
  loading?: boolean;
  error?: string | null;
  onSelect: (skill: SkillPickerItem) => void;
  onClose: () => void;
  placement?: 'top-start' | 'bottom-start';
}) {
  const listboxId = useId().replaceAll(':', '');
  const selectedIds = useMemo(
    () => new Set(selectedSkills.map(skill => skill.skillId)),
    [selectedSkills]
  );
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return skills;
    return skills.filter(
      skill =>
        skill.displayName.toLowerCase().includes(normalized) ||
        skill.handle.toLowerCase().includes(normalized) ||
        skill.qualifiedHandle.toLowerCase().includes(normalized) ||
        skill.description.toLowerCase().includes(normalized)
    );
  }, [query, skills]);
  const selectable = useMemo(
    () => filtered.filter(skill => !selectedIds.has(skill.skillId)),
    [filtered, selectedIds]
  );
  const [activeIndex, setActiveIndex] = useState(0);
  const activeSkill = selectable[activeIndex] || null;

  useEffect(() => {
    setActiveIndex(0);
  }, [open, query]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        onClose();
        return;
      }
      if (selectable.length === 0) return;
      if (
        event.key === 'ArrowDown' ||
        event.key === 'ArrowUp' ||
        event.key === 'Home' ||
        event.key === 'End'
      ) {
        event.preventDefault();
        event.stopPropagation();
        setActiveIndex(current => {
          if (event.key === 'Home') return 0;
          if (event.key === 'End') return selectable.length - 1;
          const offset = event.key === 'ArrowDown' ? 1 : -1;
          return (current + offset + selectable.length) % selectable.length;
        });
        return;
      }
      if (event.key === 'Enter' && activeSkill) {
        event.preventDefault();
        event.stopPropagation();
        onSelect(activeSkill);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [activeSkill, onClose, onSelect, open, selectable.length]);

  if (!open) return null;

  return (
    <Popover.Root
      open={open}
      autoFocus={false}
      unmountOnExit
      onOpenChange={details => {
        if (!details.open) onClose();
      }}
      positioning={{
        placement,
        strategy: 'fixed',
        flip: placement !== 'bottom-start',
        slide: true,
        sameWidth: false,
        fitViewport: true,
        gutter: 8,
        overflowPadding: 12,
      }}
    >
      <Popover.Anchor asChild>
        <Box position="absolute" inset="0" pointerEvents="none" aria-hidden="true" />
      </Popover.Anchor>
      <Portal>
        <Popover.Positioner zIndex="popover">
          <Popover.Content
            data-section="skillsPicker"
            role="listbox"
            aria-label="Available Skills"
            aria-activedescendant={activeSkill ? `${listboxId}-${activeSkill.skillId}` : undefined}
            layerStyle="surfacePopover"
            borderWidth="1px"
            borderColor="ui.border.default"
            borderRadius="glassCard"
            boxShadow="var(--attune-shadow-floating, none)"
            overflow="hidden"
            w="320px"
            maxW="calc(100vw - 24px)"
            maxH="min(var(--available-height), 332px)"
            p="1"
          >
            <Text
              px="2"
              pt="1"
              pb="1.5"
              fontSize="10px"
              lineHeight="1"
              fontWeight="700"
              letterSpacing="0.08em"
              textTransform="uppercase"
              color="ui.text.tertiary"
            >
              Skills
            </Text>
            <VisuallyHidden aria-live="polite">
              {activeSkill
                ? `${activeSkill.displayName}, ${activeSkill.scope} Skill, version ${activeSkill.version}`
                : filtered.length === 0
                  ? 'No matching Skills'
                  : ''}
            </VisuallyHidden>
            <VStack
              align="stretch"
              gap="0"
              maxH="288px"
              overflowY="auto"
              overscrollBehavior="contain"
            >
              {loading ? (
                <HStack justify="center" gap="2" minH="34px" color="ui.text.secondary">
                  <InlineSpinner />
                  <Text fontSize="xs">Loading Skills…</Text>
                </HStack>
              ) : filtered.length === 0 ? (
                <Text px="2.5" py="2.5" fontSize="xs" color="ui.text.secondary">
                  No matching Skills
                </Text>
              ) : (
                filtered.map(skill => {
                  const selected = selectedIds.has(skill.skillId);
                  return (
                    <SkillDescriptionTooltip
                      key={`${skill.skillId}:${skill.versionId}`}
                      skill={skill}
                    >
                      <Button
                        id={`${listboxId}-${skill.skillId}`}
                        role="option"
                        aria-selected={selected}
                        variant="ghost"
                        minH="34px"
                        px="2"
                        py="0"
                        gap="2"
                        justifyContent="flex-start"
                        borderRadius="glassInput"
                        bg={
                          selected || activeSkill?.skillId === skill.skillId
                            ? 'ui.surface.selected'
                            : 'transparent'
                        }
                        _hover={{ bg: 'ui.surface.input' }}
                        _focusVisible={{ outline: '1px solid', outlineColor: 'accent.focusRing' }}
                        disabled={selected}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => onSelect(skill)}
                      >
                        <CompactSkillRowContent skill={skill} selected={selected} />
                      </Button>
                    </SkillDescriptionTooltip>
                  );
                })
              )}
              {error && !loading ? (
                <Text px="2.5" py="2" fontSize="xs" color="status.warning.fg">
                  {error} Built-in Skills remain available.
                </Text>
              ) : null}
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}

export function SkillUsageBadges({
  usage,
  skills,
}: {
  usage?: AgentSkillUsage[];
  skills: SkillPickerItem[];
}) {
  if (!usage?.length) return null;
  const byId = new Map(skills.map(skill => [skill.skillId, skill]));
  return (
    <HStack mt="2.5" gap="1.5" minW={0} flexWrap="wrap" aria-label="Used Skills">
      <Text
        fontSize="2xs"
        fontWeight="700"
        color="ui.text.tertiary"
        textTransform="uppercase"
        letterSpacing="0.05em"
      >
        Used Skills
      </Text>
      {usage.map(item => {
        const skill = byId.get(item.skillId);
        return (
          <Badge
            key={`${item.skillId}:${item.versionId}:${item.activation}`}
            size="sm"
            maxW="full"
            overflow="hidden"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
            borderWidth="1px"
            {...scopeBadgeStyle(item.scope)}
            title={`${item.activation} invocation · ${item.contentSha256.slice(0, 8)}`}
          >
            {skill?.handle || `@${item.scope}:${item.skillId}`} · v{item.version} ·{' '}
            {item.activation}
          </Badge>
        );
      })}
    </HStack>
  );
}
